import { calculatePercentage, round, decimalRound, calculateSum } from "../../libraries/_helpers";
import { isObjectEmpty } from "../mls-shooting-breakdown/utils";

export const statsComparisonTypes = {
  general: 'general',
  passing: 'passing',
  attacking: 'attacking',
  defending: 'defending',
  shootingBreakdown: 'shooting-breakdown',
  passingBreakdown: 'passing-breakdown',
  expectedGoals: 'expected-goals'
};

export function formatStatsComparisonData(data, homeClubSportecId, awayClubSportecId, isSeasonStats) {
  let clubs = {};
  if (isSeasonStats) {
    clubs = formatSeasonStats(data, homeClubSportecId, awayClubSportecId);
  } else {  
    clubs = formatNonSeasonStats(data);
  }
  
  const home = mapClubToStats(clubs.homeClub, isSeasonStats);
  const away = mapClubToStats(clubs.awayClub, isSeasonStats);
  return { homeClub: home, awayClub: away };
}

function getStatsValue(isAverageValue, statsValue, gamesPlayed) {
  if (!statsValue) {
    return 0;
  }
  if (isAverageValue && gamesPlayed > 0) {
    return decimalRound(statsValue / gamesPlayed, 1);
  }
  return statsValue;
}

export function isMlsCompetitionMatch(competitionId = "") {
  const { mlsCompetitionSportecIds = "" } = window.forgeVariables.match;
  
  const mlsCompetitionIdsArray = mlsCompetitionSportecIds.split(",");

  return mlsCompetitionIdsArray.includes(competitionId.toString());
}

export function isTimeAndDateTBD(){
  return window?.forgeVariables?.match?.showDateAndTimeTbd === 'true';
}

function formatNonSeasonStats(data) {
  const clubs = {
    homeClub: {},
    awayClub: {},
  }

  if (isObjectEmpty(data) && !data?.hasOwnProperty('match_statistics_list')) {
    return clubs;
  }

  if (!(Array.isArray(data.match_statistics_list) && data.match_statistics_list.length)) {
    return clubs
  }
  
  const teamsStats = data.match_statistics_list[0]?.match_statistics?.team_statistics;
  
  if (!teamsStats || teamsStats.length !== 2) {
    return clubs;
  }

  clubs.homeClub = teamsStats.find(club => club.team_role === 'home');
  clubs.awayClub = teamsStats.find(club => club.team_role === 'away');
  
  return clubs

}

function formatSeasonStats(data,  homeClubSportecId, awayClubSportecId) {
  const clubs = {
    homeClub: {},
    awayClub: {},
  };

  if (isObjectEmpty(data) && !data?.hasOwnProperty('team_statistics')) {
    return clubs;
  }

  clubs.homeClub = data?.team_statistics?.find(club => club.team_id === homeClubSportecId);
  clubs.awayClub = data?.team_statistics?.find(club => club.team_id === awayClubSportecId);
  
  if (isObjectEmpty(clubs.homeClub) || isObjectEmpty(clubs.awayClub)) {
    return {
      homeClub: {},
      awayClub: {},
    };
  }  

  return clubs;
}

function mapClubToStats(club, isSeasonStats) {
  const {
    matches_played: games_played,
    goals_conceded,
    clean_sheet,
    xG: expected_goals,
    expected_goals_conceded,
    interceptions_sum: interception,
    shots_at_goal_inside_box: attempts_conceded_ibox,
    shots_at_goal_outside_box: attempts_conceded_obox,
    possession_ratio: possession_percentage,
    shots_at_goal_sum: total_scoring_att,
    shots_on_target: ontarget_scoring_att,
    passes_successful_sum: accurate_pass,
    passes_sum: total_pass,
    corner_kicks_sum: corner_taken,
    fouls_sum: fk_foul_lost,
    cards_yellow: total_yel_card,
    cards_red: total_red_card,
    yellow_card,
    goals,
    cards_yellow_red,
    shots_at_goal_failed: shot_off_target,
    shots_at_goal_blocked: blocked_scoring_att,
    accurate_fwd_zone_pass,
    total_fwd_zone_pass,
    successful_final_third_passes,
    total_final_third_passes,
    crosses_successful_sum: accurate_cross,
    crosses_sum: total_cross,
    crosses_from_play_sum: total_cross_nocorner,
    offsides: total_offside,
    tackling_games_air_won: duel_won,
    won_tackle,
    goalkeeper_saves: saves,
    defensive_clearances: total_clearance,
    tackling_games_air_won: aerial_won,
    accurate_pass_percentage: accurate_pass_per,
    accurate_cross_per,
    passes_from_play_successful: successful_open_play_pass,
    passes_from_play_sum: open_play_pass,
    crosses_from_standards_successful: accurate_freekick_cross,
    crosses_from_standards_sum: freekick_cross,
    crosses_from_play_successful: accurate_cross_nocorner,
    team_role,
    three_letter_code,
    team_three_letter_code,
    team_short_name,
    team_name: fullName
  } = club || {};  

  const attempts_conceded = (attempts_conceded_ibox || 0) + (attempts_conceded_obox || 0);

  const isAverageValue = isSeasonStats && games_played > 0;

  return {
    total_team_xg: round(expected_goals, 1),
    possession_percentage: round(possession_percentage, 1),
    shots: getStatsValue(isAverageValue, total_scoring_att, games_played),
    shot_on_goal_percentage: calculatePercentage(ontarget_scoring_att, total_scoring_att),
    passing_accuracy_percentage: calculatePercentage(accurate_pass, total_pass),
    goals_conceded,
    clean_sheet,
    expected_goals_conceded: round(expected_goals_conceded, 1),
    interception,
    shots_against: round(attempts_conceded, 1),
    corner_taken: getStatsValue(isAverageValue, corner_taken, games_played),
    fk_foul_lost: getStatsValue(isAverageValue, fk_foul_lost, games_played),
    total_yel_card: getStatsValue(isAverageValue, total_yel_card || yellow_card, games_played),
    total_red_card: getStatsValue(isAverageValue, calculateSum(cards_yellow_red, total_red_card), games_played),
    goals: goals || 0,
    shot_off_target: shot_off_target || 0,
    shots_on_target: getStatsValue(isAverageValue, ontarget_scoring_att, games_played),
    blocked_scoring_att: getStatsValue(isAverageValue, blocked_scoring_att, games_played),
    overall: calculatePercentage(accurate_pass, total_pass),
    attacking: calculatePercentage(accurate_fwd_zone_pass, total_fwd_zone_pass),
    finalThird: calculatePercentage(successful_final_third_passes, total_final_third_passes),
    crosses: calculatePercentage(accurate_cross, total_cross),
    total_pass: getStatsValue(isAverageValue, total_pass, games_played),
    total_cross: getStatsValue(isAverageValue, total_cross_nocorner, games_played),
    total_offside: getStatsValue(isAverageValue, total_offside, games_played),
    duel_won: getStatsValue(isAverageValue, duel_won, games_played),
    won_tackle: getStatsValue(isAverageValue, won_tackle, games_played),
    saves: getStatsValue(isAverageValue, saves, games_played),
    total_clearance: getStatsValue(isAverageValue, total_clearance, games_played),
    expected_goals: decimalRound(expected_goals, 1) || 0,
    aerial_won: aerial_won || 0,
    accurate_pass_per: decimalRound(accurate_pass_per) || 0,
    accurate_cross_per: decimalRound(accurate_cross_per) || 0,
    open_play_pass_per: calculatePercentage(successful_open_play_pass, open_play_pass),
    set_piece_cross_per: calculatePercentage(accurate_freekick_cross, freekick_cross),
    open_play_cross_per: calculatePercentage(accurate_cross_nocorner, total_cross_nocorner),
    abbreviation: team_three_letter_code || three_letter_code || '',
    shortName: team_short_name,
    fullName: fullName || '',
    team_role
  }
}